<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <router-link to="/research#trends">审计风控趋势</router-link>
      <span class="separator">/</span>
      <span class="current">AI大模型在审计领域的应用前景</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">审计风控趋势</span>
          <span class="tag">AI大模型</span>
          <span class="tag">技术趋势</span>
        </div>
        <h1 class="article-title">AI大模型在审计领域的应用前景</h1>
        <p class="article-subtitle">大模型赋能审计 · 智能报告生成 · 疑点识别 · 风险预测 · 落地实践与ROI分析</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-02-10</span>
          <span><i class="far fa-clock"></i> 阅读约 18 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">🔍</div>
        <div>
          <h3>导读</h3>
          <p>GPT-4、LLaMA、文心一言等大语言模型的爆发，正在深刻重塑审计行业的工作方式。本文深度解析大模型如何赋能审计工作——从智能审计报告生成、疑点解释，到风险预测与合规监控，覆盖全场景应用路径。同时分享国内外5家领先企业的落地实践，并给出切实可行的ROI分析框架与实施建议。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、大模型革命：审计新时代已来</a></li>
          <li><a href="#section2">二、核心应用场景全景图</a></li>
          <li><a href="#section3">三、智能审计报告生成</a></li>
          <li><a href="#section4">四、疑点识别与智能解释</a></li>
          <li><a href="#section5">五、风险预测与实时预警</a></li>
          <li><a href="#section6">六、国内外5家标杆企业落地实践</a></li>
          <li><a href="#section7">七、ROI分析与实施建议</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、大模型革命：审计新时代已来</h2>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🚀</div>
              <div>
                <h4>范式转变：从规则驱动到智能驱动</h4>
                <p>传统审计依赖人工判断与预设规则，效率瓶颈明显。大语言模型（LLM）具备跨模态理解、逻辑推理和知识综合能力，正将审计从"事后核查"升级为"智能实时监控"。</p>
              </div>
            </div>
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">73%</div>
              <div class="stat-label">全球四大会计师事务所已在审计流程中试点大模型</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">60%</div>
              <div class="stat-label">审计报告撰写时间可通过AI压缩60%</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">3×</div>
              <div class="stat-label">AI辅助下的风险异常识别召回率提升3倍</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">$4.5B</div>
              <div class="stat-label">2025年全球审计AI市场规模（Gartner预测）</div>
            </div>
          </div>

          <h3>大模型与传统审计工具的能力对比</h3>
          <div class="compare-table-wrap">
            <table class="compare-table">
              <thead>
                <tr>
                  <th>能力维度</th>
                  <th>传统审计工具</th>
                  <th>大模型赋能审计</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in compareRows" :key="row.dim">
                  <td class="dim-col">{{ row.dim }}</td>
                  <td class="old-col">{{ row.old }}</td>
                  <td class="new-col">{{ row.newVal }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、核心应用场景全景图</h2>
          <p>大模型在审计中的应用可分为四大方向：内容生成、智能理解、预测分析、合规问答。每个方向对应不同的技术能力与业务价值。</p>

          <div class="scene-grid">
            <div class="scene-card" v-for="scene in sceneCards" :key="scene.title" :style="{ borderTopColor: scene.color }">
              <div class="scene-icon">{{ scene.icon }}</div>
              <h4>{{ scene.title }}</h4>
              <p>{{ scene.desc }}</p>
              <div class="scene-tags">
                <span v-for="t in scene.tags" :key="t">{{ t }}</span>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>RAG架构是落地的关键</h4>
              <p>大模型的"幻觉"问题在审计这类高精度场景中是致命的。企业落地AI审计的核心技术是RAG（检索增强生成）——将企业内部审计准则、法规库、历史案例与大模型结合，确保生成内容有据可查、可追溯、可解释。</p>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、智能审计报告生成</h2>

          <p>审计报告撰写是审计工作中耗时最长的环节之一。一份标准的内部审计报告，从底稿整理到最终成稿，通常需要2-5天。大模型可将这个时间压缩到数小时。</p>

          <h3>3.1 报告生成技术路径</h3>
          <div class="flow-steps">
            <div class="flow-step" v-for="(step, i) in reportSteps" :key="i">
              <div class="flow-step-number" :style="{ background: step.color }">{{ i + 1 }}</div>
              <div class="flow-step-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>

          <h3>3.2 典型报告生成效果</h3>
          <div class="effect-grid">
            <div class="effect-card" v-for="e in reportEffects" :key="e.name">
              <div class="effect-icon">{{ e.icon }}</div>
              <h4>{{ e.name }}</h4>
              <p>{{ e.desc }}</p>
              <div class="effect-metric">{{ e.metric }}</div>
            </div>
          </div>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>报告自动化的边界</h4>
                <p>大模型可自动生成报告框架、填充数据验证结论、撰写风险描述。但审计结论的最终判断仍需人工复核——AI负责"起草"，审计师负责"定稿"，是目前最佳的人机协同模式。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、疑点识别与智能解释</h2>

          <p>传统审计依赖预设规则引擎发现异常，漏报率高且无法处理自然语言类证据（合同、邮件、会议纪要等）。大模型将非结构化信息纳入分析，大幅扩展疑点发现的边界。</p>

          <h3>4.1 疑点识别能力维度</h3>
          <div class="capability-matrix">
            <div class="cap-row" v-for="cap in auditCapabilities" :key="cap.type">
              <div class="cap-type" :style="{ background: cap.color }">{{ cap.type }}</div>
              <div class="cap-items">
                <div class="cap-item" v-for="(item, i) in cap.items" :key="i">
                  <div class="cap-item-name">{{ item.name }}</div>
                  <div class="cap-item-desc">{{ item.desc }}</div>
                </div>
              </div>
            </div>
          </div>

          <h3>4.2 智能解释：让审计发现"说人话"</h3>
          <div class="explain-cases">
            <div class="explain-case" v-for="c in explainCases" :key="c.finding">
              <div class="explain-finding">
                <span class="finding-label">🔍 发现</span>
                <p>{{ c.finding }}</p>
              </div>
              <div class="explain-arrow">↓ AI 智能解释</div>
              <div class="explain-result">
                <span class="result-label">📋 解释</span>
                <p>{{ c.explain }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、风险预测与实时预警</h2>

          <p>从"发现问题"到"预测问题"，是AI审计最具价值的跨越。大模型结合时序数据、图关系分析和业务规律，能够在风险事件发生前发出预警。</p>

          <div class="risk-predict-grid">
            <div class="rp-card" v-for="rp in riskPredictions" :key="rp.name" :style="{ borderLeftColor: rp.color }">
              <div class="rp-icon">{{ rp.icon }}</div>
              <h4>{{ rp.name }}</h4>
              <p class="rp-desc">{{ rp.desc }}</p>
              <div class="rp-tech">技术支撑：{{ rp.tech }}</div>
              <div class="rp-value">业务价值：{{ rp.value }}</div>
            </div>
          </div>

          <h3>5.1 预警分级与响应机制</h3>
          <div class="alert-levels">
            <div class="alert-level" v-for="level in alertLevels" :key="level.name" :style="{ background: level.bg }">
              <div class="alert-badge" :style="{ background: level.color }">{{ level.name }}</div>
              <div class="alert-info">
                <h4>{{ level.threshold }}</h4>
                <p>{{ level.action }}</p>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">⚡</div>
            <div>
              <h4>持续审计≠实时监控</h4>
              <p>持续审计强调的是"覆盖面"和"周期缩短"，从一年一次到一月一次、一周一次。真正的"实时审计"目前在大额交易、合规申报等高风险场景中已有实践，而全面实时审计涉及数据治理底座的夯实，需要2-3年建设周期。</p>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、国内外5家标杆企业落地实践</h2>

          <div class="case-cards">
            <div class="case-card" v-for="c in cases" :key="c.name">
              <div class="case-header" :style="{ background: c.bg }">
                <h3>{{ c.name }}</h3>
                <p class="case-industry">{{ c.industry }} · {{ c.region }}</p>
              </div>
              <div class="case-body">
                <div class="case-row">
                  <span class="case-label">核心诉求</span>
                  <span>{{ c.need }}</span>
                </div>
                <div class="case-row">
                  <span class="case-label">AI方案</span>
                  <span>{{ c.solution }}</span>
                </div>
                <div class="case-row">
                  <span class="case-label">量化成果</span>
                  <span class="case-result">{{ c.result }}</span>
                </div>
                <div class="case-row">
                  <span class="case-label">投入周期</span>
                  <span>{{ c.duration }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">🌟</div>
              <div>
                <h4>成功落地的共同特征</h4>
                <p>分析5家企业的实践，我们总结出三点共同成功要素：① 有明确的业务痛点驱动，而非技术驱动；② 先小场景验证、再大规模推广；③ 审计人员深度参与AI系统设计，确保系统输出符合审计逻辑。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、ROI分析与实施建议</h2>

          <h3>7.1 ROI测算框架</h3>
          <div class="roi-grid">
            <div class="roi-card roi-benefit">
              <h4>💰 收益测算项</h4>
              <ul>
                <li v-for="(b, i) in roiBenefits" :key="i">
                  <span class="roi-item-name">{{ b.name }}</span>
                  <span class="roi-item-val benefit">{{ b.val }}</span>
                </li>
              </ul>
            </div>
            <div class="roi-card roi-cost">
              <h4>📊 成本测算项</h4>
              <ul>
                <li v-for="(c, i) in roiCosts" :key="i">
                  <span class="roi-item-name">{{ c.name }}</span>
                  <span class="roi-item-val cost">{{ c.val }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="roi-summary">
            <div class="roi-sum-item" v-for="s in roiSummary" :key="s.label">
              <div class="roi-sum-value" :style="{ color: s.color }">{{ s.value }}</div>
              <div class="roi-sum-label">{{ s.label }}</div>
            </div>
          </div>

          <h3>7.2 实施路线图</h3>
          <div class="impl-timeline">
            <div class="impl-phase" v-for="(phase, i) in implPhases" :key="i">
              <div class="impl-phase-header" :style="{ background: phase.color }">
                <span class="impl-phase-num">阶段 {{ i + 1 }}</span>
                <span class="impl-phase-name">{{ phase.title }}</span>
                <span class="impl-phase-time">{{ phase.duration }}</span>
              </div>
              <div class="impl-phase-body">
                <p>{{ phase.desc }}</p>
                <div class="impl-deliverable">交付物：{{ phase.deliverable }}</div>
              </div>
            </div>
          </div>

          <h3>7.3 风险与应对</h3>
          <div class="risk-grid">
            <div class="risk-item" v-for="r in implementRisks" :key="r.risk">
              <div class="risk-title">⚠️ {{ r.risk }}</div>
              <div class="risk-solution">应对：{{ r.solution }}</div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top:32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语：人机协同是最优形态</h4>
                <p>大模型不会取代审计师，但会深刻改变审计师的工作方式。未来3年，"AI做筛查、人工做判断"的人机协同模式将成为主流。率先建立AI审计能力的企业，将在合规效率、风险洞察和决策质量上获得显著竞争优势。古杉数据已协助多家央企完成AI审计体系建设，欢迎交流实践经验。</p>
              </div>
            </div>
          </div>
        </section>

      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>探索 AI 审计解决方案？</h2>
        <p>古杉数据提供从大模型选型、审计知识库建设到系统集成的端到端服务</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约 AI 审计专家</router-link>
          <router-link to="/consulting/audit-risk-consulting" class="btn-outline">了解审计风控咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'AiAuditTrend',
  data () {
    return {
      compareRows: [
        { dim: '自然语言处理', old: '不支持，无法分析合同/邮件', newVal: '深度理解长文本，提取关键审计信息' },
        { dim: '规则覆盖', old: '依赖预设规则，新型风险漏报', newVal: '结合业务知识库，动态识别异常模式' },
        { dim: '报告撰写', old: '全靠人工，2-5天/份', newVal: 'AI起草初稿，审计师复核，数小时完成' },
        { dim: '知识更新', old: '规则维护成本高，滞后', newVal: 'RAG检索最新法规与案例，实时更新' },
        { dim: '可解释性', old: '黑盒规则，难以解释', newVal: '给出证据链与推理过程，可审计' }
      ],
      sceneCards: [
        { icon: '📝', title: '内容生成', desc: '自动生成审计报告、底稿摘要、风险说明书，大幅降低报告撰写工时。', tags: ['报告生成', '底稿摘要', '问询函起草'], color: '#1A73E8' },
        { icon: '🔍', title: '智能理解', desc: '跨越非结构化数据壁垒，理解合同条款、会议纪要、邮件往来中的审计线索。', tags: ['合同审查', '邮件分析', '凭证识别'], color: '#10B981' },
        { icon: '🎯', title: '预测分析', desc: '基于历史数据与行业模式，提前预判舞弊风险、合规隐患和经营异常。', tags: ['舞弊预测', '风险评分', '异常检测'], color: '#F59E0B' },
        { icon: '💬', title: '合规问答', desc: '构建企业级审计知识库，回答审计准则、监管要求、内部政策相关问题。', tags: ['政策问答', '准则解读', '案例查询'], color: '#8B5CF6' }
      ],
      reportSteps: [
        { title: '数据采集与预处理', desc: '从ERP、财务系统、OA等采集底稿数据，经ETL清洗后结构化入库。', color: '#4A90D9' },
        { title: '审计底稿分析', desc: '大模型读取底稿，识别关键数字、异常波动、逻辑矛盾，生成分析摘要。', color: '#1F6FEB' },
        { title: '知识库检索（RAG）', desc: '检索企业审计准则库、监管法规库、历史案例库，确保结论有据可查。', color: '#0E4EAD' },
        { title: '报告框架生成', desc: '按审计报告标准模板，自动填充摘要、发现、建议、附件引用等章节。', color: '#0A3A82' },
        { title: '人工复核签发', desc: '审计师在AI起草基础上修订确认，数字签名后正式存档发布。', color: '#0E1F52' }
      ],
      reportEffects: [
        { icon: '⏱', name: '效率提升', desc: '标准内审报告生成时间', metric: '从3天压缩至4小时' },
        { icon: '✅', name: '质量提升', desc: '关键信息遗漏率', metric: '下降 76%' },
        { icon: '📚', name: '知识复用', desc: '历史审计案例自动关联引用', metric: '覆盖率 > 85%' },
        { icon: '🔄', name: '一致性', desc: '同类审计结论表述一致性', metric: '从 62% 提升至 97%' }
      ],
      auditCapabilities: [
        {
          type: '结构化数据',
          color: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
          items: [
            { name: '财务数据异常检测', desc: '识别异常的记账分录、金额突增突降、循环交易' },
            { name: '关联交易分析', desc: '识别隐性关联方，分析交易定价公允性' },
            { name: '指标趋势预警', desc: '基于历史基线，对超阈值指标自动告警' }
          ]
        },
        {
          type: '非结构化数据',
          color: 'linear-gradient(135deg, #10B981, #059669)',
          items: [
            { name: '合同条款风险识别', desc: '自动标注违约条款、不合理条件、缺失关键保护条款' },
            { name: '邮件往来分析', desc: '识别业务往来中的利益冲突信号、不当承诺' },
            { name: '会议纪要合规审查', desc: '比对决策流程与治理要求，识别越权审批' }
          ]
        }
      ],
      explainCases: [
        {
          finding: '某子公司2025年Q4预付账款余额较Q3环比增长312%，明显超出行业基准。',
          explain: '该子公司于2025年11月新签两份大额采购框架合同（共计8,200万元），合同要求预付30%定金，导致预付账款集中增加。建议核实合同真实性及供应商资质，重点关注是否存在虚开发票或利益输送风险。'
        },
        {
          finding: '费用报销系统检测到3名员工同一笔差旅在不同时间点重复报销，总金额42,600元。',
          explain: '系统通过语义比对识别出3份报销单的出行目的地、时间区间和金额具有高度相似性（相似度＞91%）。初步判断为重复报销，建议审计人员调取原始发票比对，并对报销审批链路进行追溯核查。'
        }
      ],
      riskPredictions: [
        { icon: '💳', name: '舞弊风险预测', desc: '结合员工行为数据、财务异常信号、关联方图谱，构建舞弊风险评分模型。', tech: 'LLM + 图神经网络 + 时序分析', value: '提前3-6个月预警高风险个体', color: '#DC2626' },
        { icon: '📊', name: '经营风险预警', desc: '分析行业数据、竞对动态、宏观政策，预判经营指标偏离趋势。', tech: 'RAG + 时序预测模型', value: '经营异常预警准确率 > 82%', color: '#F59E0B' },
        { icon: '⚖️', name: '合规风险监控', desc: '实时比对最新监管要求与企业实际操作，自动识别合规缺口。', tech: 'LLM + 规则引擎', value: '合规缺口发现速度提升5倍', color: '#3B82F6' }
      ],
      alertLevels: [
        { name: '红色预警', threshold: '风险评分 ≥ 85', action: '立即启动专项审计，24小时内响应', color: '#DC2626', bg: '#FEF2F2' },
        { name: '橙色预警', threshold: '风险评分 60-84', action: '纳入下一轮审计重点关注，7天内制定核查方案', color: '#EA580C', bg: '#FFF7ED' },
        { name: '黄色预警', threshold: '风险评分 40-59', action: '持续监控，季度审计例行覆盖', color: '#CA8A04', bg: '#FEFCE8' },
        { name: '绿色正常', threshold: '风险评分 < 40', action: '年度例行审计，无需特别关注', color: '#16A34A', bg: '#F0FDF4' }
      ],
      cases: [
        {
          name: '德勤（Deloitte）',
          industry: '专业服务', region: '全球',
          bg: 'linear-gradient(135deg, #0E1F52, #1F3F8F)',
          need: '大规模外部审计中的财务报表分析效率提升',
          solution: '基于GPT-4部署"Deloitte AI"，自动分析财报、生成审计询问函、底稿初审',
          result: '审计底稿处理效率提升50%，差错率下降40%，率先在全球30个国家部署',
          duration: '18个月 MVP到规模落地'
        },
        {
          name: '某国有大型商业银行',
          industry: '银行', region: '中国',
          bg: 'linear-gradient(135deg, #10B981, #059669)',
          need: '内部审计覆盖面不足（仅15%业务被年度审计覆盖）',
          solution: '基于文心一言+私有化部署，建立持续审计引擎，每日自动扫描交易数据',
          result: '审计覆盖率从15%提升至78%，疑点线索自动发现量增加8倍，节省审计人力30%',
          duration: '12个月'
        },
        {
          name: '某央企能源集团',
          industry: '能源', region: '中国',
          bg: 'linear-gradient(135deg, #F59E0B, #D97706)',
          need: '120+子公司合同审查人力严重不足，合规风险高',
          solution: '部署合同AI审查系统，大模型自动提取关键条款、识别异常风险点',
          result: '合同审查效率提升12倍，风险合同识别准确率91%，每年节省审计人力约40人年',
          duration: '6个月'
        },
        {
          name: 'KPMG毕马威',
          industry: '专业服务', region: '全球',
          bg: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
          need: '审计准则知识更新快，审计师答疑效率低',
          solution: '构建审计知识库RAG系统，涵盖IFRS、GAAP、中国准则及内部案例',
          result: '审计师准则查询效率提升10倍，新人培训周期缩短40%，客户服务响应时间下降65%',
          duration: '9个月'
        },
        {
          name: '某头部保险集团',
          industry: '保险', region: '中国',
          bg: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
          need: '理赔欺诈识别率低，人工审查成本高',
          solution: 'LLM+图计算，构建欺诈关系网络，结合非结构化理赔材料综合分析',
          result: '欺诈识别召回率从52%提升至89%，每年挽回欺诈损失超2.3亿元，人工复核量下降60%',
          duration: '10个月'
        }
      ],
      roiBenefits: [
        { name: '审计人力节省', val: '30-50 人年/年' },
        { name: '审计覆盖率提升带来的风险损失减少', val: '500万-5000万/年' },
        { name: '报告生成效率提升', val: '节省约 2000 小时/年' },
        { name: '合规罚款风险降低', val: '视行业而定' }
      ],
      roiCosts: [
        { name: '大模型API/私有化部署', val: '50-300万/年' },
        { name: '知识库建设与维护', val: '30-100万/年' },
        { name: '定制开发与集成', val: '100-500万（一次性）' },
        { name: '人员培训', val: '10-30万/年' }
      ],
      roiSummary: [
        { value: '12-18个月', label: '典型投资回收期', color: '#1A73E8' },
        { value: '3-8×', label: '3年ROI倍数（中大型企业）', color: '#10B981' },
        { value: '第2年起', label: '规模效益开始显现', color: '#F59E0B' }
      ],
      implPhases: [
        { title: '痛点诊断与场景选择', duration: '1-2个月', desc: '梳理审计流程痛点，选择1-2个高价值、低风险的 AI 切入场景，明确数据基础和集成边界。', deliverable: '场景优先级矩阵 + 可行性评估报告' },
        { title: 'MVP快速验证', duration: '2-3个月', desc: '选定小范围（如单一子公司或单一业务线）快速部署AI审计模块，收集真实反馈，验证ROI假设。', deliverable: 'MVP系统 + 效果评估报告' },
        { title: '规模化推广', duration: '3-6个月', desc: '基于MVP经验，扩展到更多场景与组织范围，完善知识库、优化模型、建立运营机制。', deliverable: '生产环境系统 + 运营手册' },
        { title: '持续优化运营', duration: '持续', desc: '建立AI审计知识库持续更新机制，定期模型迭代，形成"审计发现→模型改进"的正向循环。', deliverable: '季度效果报告 + 知识库更新记录' }
      ],
      implementRisks: [
        { risk: '大模型"幻觉"导致审计错误', solution: '强制RAG溯源，所有生成内容附带证据引用，人工终审不可省略' },
        { risk: '数据安全与隐私合规', solution: '采用私有化部署或混合云，财务数据禁止上传公有云大模型' },
        { risk: '审计人员抵触AI工具', solution: '设计"AI辅助而非替代"的工作流，让审计师感受到效率提升而非威胁' },
        { risk: '知识库老化失效', solution: '建立法规库自动更新机制，季度人工审查确保内容准确性' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 1.8rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 对比表格 */
.compare-table-wrap { overflow-x: auto; margin: 24px 0; }
.compare-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.compare-table th { padding: 12px 16px; text-align: left; font-weight: 600; }
.compare-table th:first-child { background: #0E1F52; color: #fff; }
.compare-table th:nth-child(2) { background: #5A5E66; color: #fff; }
.compare-table th:nth-child(3) { background: #1A73E8; color: #fff; }
.compare-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; vertical-align: top; }
.compare-table tr:nth-child(even) td { background: #F6F8FC; }
.dim-col { font-weight: 600; color: #0E1F52; }
.old-col { color: #5A5E66; }
.new-col { color: #1A73E8; font-weight: 500; }

/* 场景卡片 */
.scene-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.scene-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; border-top: 3px solid; }
.scene-icon { font-size: 2rem; margin-bottom: 8px; }
.scene-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.scene-card p { font-size: 13px; color: #5A5E66; line-height: 1.5; margin-bottom: 12px; }
.scene-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.scene-tags span { background: #EBF3FE; color: #1A73E8; padding: 3px 10px; border-radius: 6px; font-size: 11px; }

/* 流程步骤 */
.flow-steps { position: relative; padding-left: 40px; margin: 24px 0; }
.flow-steps::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #0E1F52); border-radius: 2px; }
.flow-step { position: relative; margin-bottom: 16px; }
.flow-step-number { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,.15); }
.flow-step-content { background: #F6F8FC; border-radius: 12px; padding: 16px 20px; border: 1px solid #E8ECF0; }
.flow-step-content h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.flow-step-content p { font-size: 13px; color: #5A5E66; line-height: 1.5; margin: 0; }

/* 效果卡片 */
.effect-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.effect-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.effect-icon { font-size: 2rem; margin-bottom: 8px; }
.effect-card h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.effect-card p { font-size: 12px; color: #5A5E66; margin-bottom: 8px; }
.effect-metric { font-size: 14px; font-weight: 700; color: #1A73E8; }

/* 能力矩阵 */
.capability-matrix { margin: 24px 0; }
.cap-row { display: flex; gap: 16px; margin-bottom: 16px; }
.cap-type { color: #fff; padding: 12px 16px; border-radius: 12px; font-size: 14px; font-weight: 700; flex-shrink: 0; width: 110px; display: flex; align-items: center; justify-content: center; text-align: center; }
.cap-items { display: flex; gap: 12px; flex: 1; }
.cap-item { background: #F6F8FC; border-radius: 10px; padding: 14px; border: 1px solid #E8ECF0; flex: 1; }
.cap-item-name { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.cap-item-desc { font-size: 12px; color: #5A5E66; line-height: 1.5; }

/* 解释案例 */
.explain-cases { margin: 24px 0; }
.explain-case { background: #F6F8FC; border-radius: 12px; padding: 20px; margin-bottom: 16px; border: 1px solid #E8ECF0; }
.explain-finding { margin-bottom: 10px; }
.finding-label { font-size: 12px; font-weight: 700; background: #FEF2F2; color: #DC2626; padding: 2px 8px; border-radius: 4px; margin-right: 8px; }
.explain-finding p { display: inline; font-size: 14px; color: #3D4150; }
.explain-arrow { font-size: 12px; color: #1A73E8; font-weight: 700; margin: 8px 0; padding-left: 8px; }
.explain-result { margin-top: 10px; }
.result-label { font-size: 12px; font-weight: 700; background: #EBF3FE; color: #1A73E8; padding: 2px 8px; border-radius: 4px; margin-right: 8px; }
.explain-result p { display: inline; font-size: 14px; color: #3D4150; line-height: 1.6; }

/* 风险预测 */
.risk-predict-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.rp-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; border-left: 4px solid; }
.rp-icon { font-size: 2rem; margin-bottom: 8px; }
.rp-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.rp-desc { font-size: 14px; color: #5A5E66; margin-bottom: 10px; line-height: 1.5; }
.rp-tech { font-size: 12px; color: #8B5CF6; margin-bottom: 4px; }
.rp-value { font-size: 12px; color: #10B981; font-weight: 600; }

/* 预警级别 */
.alert-levels { margin: 24px 0; }
.alert-level { display: flex; align-items: center; gap: 16px; padding: 16px 20px; border-radius: 12px; margin-bottom: 10px; }
.alert-badge { color: #fff; padding: 6px 16px; border-radius: 8px; font-size: 14px; font-weight: 700; flex-shrink: 0; min-width: 80px; text-align: center; }
.alert-info h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 2px; }
.alert-info p { font-size: 13px; color: #5A5E66; margin: 0; }

/* 案例卡片 */
.case-cards { display: grid; grid-template-columns: repeat(1, 1fr); gap: 24px; margin: 24px 0; }
.case-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.case-header { padding: 20px 24px; color: #fff; }
.case-header h3 { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
.case-industry { font-size: 13px; opacity: 0.85; margin: 0; }
.case-body { padding: 16px 24px; }
.case-row { display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px solid #F0F0F0; font-size: 14px; }
.case-row:last-child { border-bottom: none; }
.case-label { color: #5A5E66; font-weight: 600; flex-shrink: 0; min-width: 72px; }
.case-result { color: #10B981; font-weight: 600; }

/* ROI */
.roi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 24px 0; }
.roi-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; }
.roi-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 16px; }
.roi-card ul { list-style: none; padding: 0; margin: 0; }
.roi-card li { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #E8ECF0; font-size: 14px; }
.roi-card li:last-child { border-bottom: none; }
.roi-item-name { color: #3D4150; }
.roi-item-val { font-weight: 700; }
.roi-item-val.benefit { color: #10B981; }
.roi-item-val.cost { color: #DC2626; }
.roi-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.roi-sum-item { background: #F6F8FC; border-radius: 12px; padding: 20px; text-align: center; border: 1px solid #E8ECF0; }
.roi-sum-value { font-size: 1.5rem; font-weight: 900; margin-bottom: 6px; }
.roi-sum-label { font-size: 13px; color: #5A5E66; }

/* 实施路线图 */
.impl-timeline { margin: 24px 0; }
.impl-phase { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; margin-bottom: 12px; }
.impl-phase-header { display: flex; align-items: center; gap: 12px; padding: 14px 20px; color: #fff; }
.impl-phase-num { font-size: 12px; opacity: 0.8; }
.impl-phase-name { font-size: 16px; font-weight: 700; flex: 1; }
.impl-phase-time { font-size: 12px; background: rgba(255,255,255,.2); padding: 3px 10px; border-radius: 6px; }
.impl-phase-body { padding: 16px 20px; background: #F6F8FC; }
.impl-phase-body p { font-size: 14px; color: #3D4150; margin-bottom: 8px; line-height: 1.5; }
.impl-deliverable { font-size: 13px; color: #1A73E8; font-weight: 500; }

/* 风险清单 */
.risk-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 24px 0; }
.risk-item { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.risk-title { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.risk-solution { font-size: 14px; color: #3D4150; line-height: 1.5; }

/* CTA */
.cta-section { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); border-radius: 20px; padding: 48px; text-align: center; color: #fff; margin-top: 48px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 1rem; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #fff; color: #0E1F52; padding: 14px 32px; border-radius: 10px; font-weight: 700; text-decoration: none; font-size: 15px; transition: all 0.2s; }
.btn-primary:hover { background: #EBF3FE; }
.btn-outline { border: 2px solid rgba(255,255,255,.6); color: #fff; padding: 14px 32px; border-radius: 10px; font-weight: 700; text-decoration: none; font-size: 15px; transition: all 0.2s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,.1); }
</style>
